import {
  Box,
  Collapse,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import BrotherPrint from "Pages/Printing/BrotherPrint";
import DymoLabel from "Pages/Printing/DymoPrint";
import printBarcode from "Pages/Printing/ZebraPrint";
import { memo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import Status from "Shared/Common/Status";
import useLang from "Shared/hooks/useLanguage";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";
import { StyledDropButton, StyledDropMenu } from "Utils/Style/Dropdownstyle";
import { savePdfUrls } from "../../../Redux/Actions/Index";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import { AddIcon, RemoveICon } from "../../../Shared/Icons";
import { useResultDataContext } from "../../../Shared/OncologyResultDataContext";
import { dateFormatConversion } from "../../../Utils/Common/viewRequisitiontabs";
import { NormalizedData } from "../SingleRequisition/ViewReq/Row";
import ResultDataExpandableRow from "./ResultDataExpandableRow";

const Row = (props: any) => {
  const { t } = useLang();

  const {
    data,
    filterData,
    selectedBox,
    rowsToExpand,
    setSelectedBox,
    setRowsToExpand,
  } = useResultDataContext();

  const [anchorEl, setAnchorEl] = useState({
    dropdown1: null,
    dropdown2: null,
    dropdown3: null,
    dropdown4: null,
  });

  const openDrop =
    Boolean(anchorEl.dropdown1) ||
    Boolean(anchorEl.dropdown2) ||
    Boolean(anchorEl.dropdown3) ||
    Boolean(anchorEl.dropdown4);

  const requisitionStatusClassMap: Record<string, string> = {
    [t("Specimen Collected")]: "badge-status-specimen-collected",
    [t("Processing")]: "badge-status-processing",
    [t("Result Available")]: "badge-status-result-available",
    [t("Complete")]: "badge-status-complete",
    [t("Deleted")]: "badge-status-deleted",
    [t("Validated")]: "badge-status-validated",
    [t("Save For Signature")]: "badge-status-waiting-for-Signature",
    [t("On Hold")]: "badge-status-hold",
    [t("Missing Info")]: "badge-status-missing-info",
    [t("In Transit")]: "badge-status-in-transit",
    [t("Canceled")]: "badge-status-canceled",
    [t("Approved")]: "badge-status-approved",
    [t("Pending")]: "badge-status-pending",
    [t("Rejected")]: "badge-status-rejected",
    [t("Shipped")]: "badge-status-shipped",
  };

  const handleClick = (event: any, dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: event.currentTarget });
  };

  const handleClose = (dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: null });
  };

  const getPrinterContentData = async (payload: any) => {
    try {
      const printerContent = await RequisitionType.getPrinterContent(payload);
      return printerContent?.data?.data;
    } catch (error) {
      console.error(error);
    }
  };

  const PrintLabel = async (option: any, printerId: number) => {
    const objToSend = {
      printerId: printerId,
      contentList: [
        {
          requisitionOrderId: NormalizedData(
            props?.RowData,
            "requisitionorderid"
          ),
          requisitionId: NormalizedData(props?.RowData, "requisitionid"),
          //accessionNumber: NormalizedData(props?.RowData, "accessionnumber"),
        },
      ],
    };
    const content = await getPrinterContentData(objToSend);
    if (option?.includes("zebra") || option?.includes("Zebra")) {
      let i: any;
      for (i = 0; i < content.length; i++) {
        printBarcode(content[i]);
      }
      handleClose("dropdown4");
      return;
    }
    if (option.includes("dymo") || option.includes("Dymo")) {
      let i: any;
      for (i = 0; i < content.length; i++) {
        DymoLabel(content[i]);
      }
      handleClose("dropdown4");
      return;
    }
    if (option.includes("brother") || option.includes("Brother")) {
      BrotherPrint(content);
      handleClose("dropdown4");
      return;
    } else {
      toast.error(t("Configuration not available"));
      return;
    }
  };

  const dispatch = useDispatch();
  const rowExpandKey = `${props?.RowData?.requisitionOrderId}_${props?.RowData?.panelID}`;

  const handleSelectedResultDataIds = (checked: boolean, item: any) => {
    const {
      requisitionId,
      requisitionTypeId,
      facilityId,
      requisitionOrderId,
      resultFile,
      panelID,
    } = item; // Destructure id and accessionNumber from item
    if (checked) {
      setSelectedBox((pre: any) => {
        const exists = pre.requisitionOrderId.some(
          (selectedItem: any) =>
            selectedItem.requisitionOrderId === requisitionOrderId &&
            selectedItem.panelId === panelID
        );
        if (exists) return pre;
        return {
          ...pre,
          requisitionOrderId: [
            ...pre.requisitionOrderId,
            {
              requisitionId,
              reqTypeId: requisitionTypeId,
              facilityId,
              requisitionOrderId,
              resultFile,
              panelId: panelID
            },
          ], // Add an object with id and accessionNumber
        };
      });
    } else {
      setSelectedBox((prev: any) => ({
        ...prev,
        requisitionOrderId: prev.requisitionOrderId.filter(
          (selectedItem: any) =>
            selectedItem.requisitionId !== item.requisitionId ||
            selectedItem.reqTypeId !== item.requisitionTypeId ||
            selectedItem.facilityId !== item.facilityId ||
            selectedItem.requisitionOrderId !== item.requisitionOrderId ||
            selectedItem.panelId !== item.panelID
        ),
      }));
    }
  };

  const openInNewTab = (url: any) => {
    window.open(url, "_blank", "noreferrer");
  };

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        {filterData.tabId === 1 || filterData.tabId === 2 ? (
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px"
            >
              {rowsToExpand?.includes(rowExpandKey) ? (
                <button
                  onClick={() => {
                    if (rowsToExpand?.includes(rowExpandKey)) {
                      setRowsToExpand(
                        rowsToExpand.filter(
                          (val) => val !== rowExpandKey
                        )
                      );
                    }
                  }}
                  id={`ResultDataHide_${props?.RowData.requisitionOrderId}_${props?.RowData?.panelID}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                >
                  <RemoveICon />
                </button>
              ) : (
                <button
                  onClick={() => {
                    setRowsToExpand((prev) => [...prev, rowExpandKey]);
                  }}
                  id={`ResultDataShow_${props?.RowData.requisitionOrderId}_${props?.RowData?.panelID}`}
                  className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                >
                  <AddIcon />
                </button>
              )}
            </IconButton>
          </TableCell>
        ) : null}
        <TableCell style={{ width: "49px" }}>
          <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
            <input
              id={`ResultDataCheckBox_${props?.RowData.requisitionOrderId}`}
              className="form-check-input"
              type="checkbox"
              checked={selectedBox?.requisitionOrderId?.find(
                (item: any) =>
                  item?.requisitionOrderId ===
                  props?.RowData?.requisitionOrderId &&
                  item?.panelId === props?.RowData?.panelID
              )}
              onChange={(e) =>
                handleSelectedResultDataIds(e.target.checked, props?.RowData)
              }
            />
          </label>
        </TableCell>
        {props?.tabsInfo &&
          props?.tabsInfo.map((tabData: any) => (
            <>
              {tabData.isShowOnUi && !tabData.isExpandData && tabData.isShow ? (
                <>
                  {tabData?.columnKey === "view" ? (
                    <TableCell className="text-center">
                      <div className="d-flex justify-content-center">
                        <PermissionComponent
                          moduleName="ID LIS"
                          pageName="Result Data"
                          permissionIdentifier="View"
                        >
                          {({ pageName, moduleName, permissionIdentifier }) => (
                            <button
                              id={`ResultDataView_${props?.RowData.requisitionOrderId}`}
                              role="link"
                              className="btn btn-sm fw-bold btn-warning fs-12px py-0 fw-500 align-items-center d-flex"
                              onClick={() => {
                                const tokenData: any =
                                  sessionStorage.getItem("userinfo");
                                localStorage.setItem("userinfo", tokenData);
                                openInNewTab(
                                  `/OrderView/${btoa(
                                    props?.RowData?.requisitionId
                                  )}/${btoa(props.RowData?.requisitionOrderId)}`
                                );
                              }}
                            >
                              {getPermissionDisplayName(
                                pageName,
                                moduleName,
                                permissionIdentifier,
                                "View"
                              )}
                            </button>
                          )}
                        </PermissionComponent>
                      </div>
                    </TableCell>
                  ) : props?.RowData?.[tabData?.columnKey]?.includes(
                    "https://truemedpo.blob.core.windows.net"
                  ) ||
                    props?.RowData?.[tabData?.columnKey]?.includes(
                      "https://digitalrequsitions.blob.core.windows.net"
                    ) ? (
                    <TableCell
                      id={`ResultDataResultFileCell_${props?.RowData.requisitionOrderId}`}
                    >
                      <div className="d-flex justify-content-center">
                        {props.RowData?.resultFile && filterData.tabId !== 1 ? (
                          <Link to={`/docs-viewer`} target="_blank">
                            <i
                              id={`ResultDataResultFile_${props?.RowData.requisitionOrderId}`}
                              className="bi bi-file-earmark-pdf text-danger fa-2x cursor-pointer"
                              onClick={() => {
                                dispatch(
                                  savePdfUrls(props?.RowData?.resultFile)
                                );
                              }}
                            ></i>
                          </Link>
                        ) : null}
                      </div>
                    </TableCell>
                  ) : tabData?.columnKey === "lisStatus" ? (
                    <TableCell
                      id={`ResultDataStatus_${props?.RowData.requisitionOrderId}`}
                    >
                      <Status
                        cusText={props?.RowData?.[tabData?.columnKey]}
                        cusClassName={
                          props?.RowData?.[tabData?.columnKey] === t("Pending")
                            ? "badge-status-pending"
                            : props?.RowData?.[tabData?.columnKey] ===
                              t("Ready to Publish")
                              ? "badge-status-ready-to-publish"
                              : props?.RowData?.[tabData?.columnKey] ===
                                t("Final")
                                ? "badge-status-final"
                                : props?.RowData?.[tabData?.columnKey] ===
                                  t("Corrected")
                                  ? "badge-status-corrected"
                                  : "badge-status-default"
                        }
                      />
                    </TableCell>
                  ) : tabData?.columnKey?.toLowerCase() === "printlabel" ? (
                    <TableCell
                      id={`IDResultDataPrintLabel_${props?.RowData.requisitionOrderId}`}
                    >
                      <div style={{ width: "max-content" }}>
                        <StyledDropButton
                          id="PrintLabelButton"
                          aria-controls={
                            openDrop ? "demo-positioned-menu4" : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openDrop ? "true" : undefined}
                          onClick={(event) => handleClick(event, "dropdown4")}
                          className="btn btn-dark-brown btn-sm py-0 fw-400 fa-1x min-h-25px h-25px w-100px"
                        >
                          {t("Print Label")}
                          <span className="svg-icon svg-icon-5 m-0">
                            <ArrowBottomIcon />
                          </span>
                        </StyledDropButton>
                        <StyledDropMenu
                          aria-labelledby="demo-positioned-button4"
                          anchorEl={anchorEl.dropdown4}
                          open={Boolean(anchorEl.dropdown4)}
                          onClose={() => handleClose("dropdown4")}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          className="w-auto"
                        >
                          {data?.printersInfo?.map((option: any) => (
                            <MenuItem
                              onClick={() =>
                                PrintLabel(option?.label, option.value)
                              }
                              className="w-auto"
                              key={option.value}
                              value={t(option.value)}
                            >
                              <i className="fa fa fa-print text-warning mr-2 w-20px"></i>
                              {option?.label}
                            </MenuItem>
                          ))}
                        </StyledDropMenu>
                      </div>
                    </TableCell>
                  ) : tabData?.columnKey.toLowerCase() ===
                    "requisitionstatus" ? (
                    <TableCell
                      id={`RequisitionStatus_${NormalizedData(
                        props?.RowData,
                        "requisitionorderid"
                      )}`}
                      sx={{ width: "max-content", textAlign: "center" }}
                    >
                      <Status
                        cusText={props?.RowData?.[tabData?.columnKey]}
                        cusClassName={
                          requisitionStatusClassMap[
                          props?.RowData?.[tabData?.columnKey]
                          ] || "badge-status-default"
                        }
                      />
                    </TableCell>
                  ) : (
                    <TableCell
                      id={`ResultData${tabData?.columnKey}_${props?.RowData.requisitionOrderId}`}
                    >
                      {dateFormatConversion(props?.RowData, tabData?.columnKey)}
                    </TableCell>
                  )}
                </>
              ) : null}
            </>
          ))}
      </TableRow>
      <TableRow>
        <TableCell colSpan={13} className="padding-0">
          <Collapse
            in={rowsToExpand?.includes(rowExpandKey)}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{ margin: 1 }}>
              <Typography gutterBottom component="div">
                <div className="row">
                  <div className="col-lg-12 bg-white">
                    <ResultDataExpandableRow
                      row={props?.RowData}
                      requisitionId={props?.RowData.requisitionId}
                      reqTypeId={props?.RowData.requisitionTypeId}
                      facilityId={props?.RowData.facilityId}
                      requisitionOrderId={props?.RowData?.requisitionOrderId}
                      PathogensList={props?.RowData?.pathogens}
                      key={props?.RowData?.requisitionOrderId}
                      panelId={props?.RowData?.panelID}
                    />
                  </div>
                </div>
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default memo(Row);
